package com.dignicate.web.data.markdown.parser

import com.dignicate.web.data.markdown.model.MarkdownBlock
import com.dignicate.web.data.markdown.model.ParsedMarkdownDocument
import com.dignicate.web.data.markdown.parser.core.ParsingContext
import com.dignicate.web.data.markdown.parser.handlers.BlankLineHandler
import com.dignicate.web.data.markdown.parser.handlers.CodeFenceHandler
import com.dignicate.web.data.markdown.parser.handlers.FrontMatterHandler
import com.dignicate.web.data.markdown.parser.handlers.HeadingHandler
import com.dignicate.web.data.markdown.parser.handlers.ParagraphHandler
import com.dignicate.web.data.markdown.parser.handlers.UnorderedListHandler
import com.dignicate.web.domain.Language

object SimpleMarkdownParser {

    /**
     * This list defines all implemented markdown syntaxes.
     * The order is important for parsing priority.
     */
    private val handlers = listOf(
        HeadingHandler(),
        UnorderedListHandler(),
        CodeFenceHandler(),
        ParagraphHandler(),
        BlankLineHandler(),
    )

    fun parse(slug: String, lang: Language, rawMarkdown: String): ParsedMarkdownDocument {
        val normalizedText = rawMarkdown.replace("\r\n", "\n").replace("\r", "\n")
        val lines = normalizedText.split('\n')
        val ctx = ParsingContext(lines)

        // Handle front matter separately as it only appears at the beginning.
        val frontMatterHandler = FrontMatterHandler()
        if (frontMatterHandler.canHandle(ctx)) {
            frontMatterHandler.handle(ctx)
        }

        while (!ctx.isEof()) {
            val handler = handlers.firstOrNull { it.canHandle(ctx) }
            if (handler != null) {
                handler.handle(ctx)
            } else {
                // Failsafe to prevent infinite loops
                ctx.index++
            }
        }

        val title = ctx.blocks.filterIsInstance<MarkdownBlock.Heading>()
            .firstOrNull { it.level == 1 }?.text

        return ParsedMarkdownDocument(
            slug = slug,
            lang = lang,
            metadata = ctx.metadata,
            title = title,
            blocks = ctx.blocks,
            raw = rawMarkdown
        )
    }
}
